import React from 'react';

/* ──────────────────────────────────────────────────────────────────────────
   Boss hieroglyphs — one bespoke mark per deck-level boss.

   DESIGN LANGUAGE (provenance: the skill-tree keystone glyphs in
   keystoneCards.jsx + wiki/reference/debono-skill-tree-node-svg-tooling.md):
   "conceptual-but-simple beats literal-but-busy — each glyph encodes the
   EFFECT/CONCEPT, not a literal depiction." So each boss glyph encodes the
   COMBAT *DYNAMIC* of that fight (how it feels to fight), NOT the
   pathophysiology of the disease. Same idiom as the keystones: authored in a
   24x24 box, stroke-only line-art, weight 1.8, round caps/joins. Color is
   inherited from the node (currentColor) — never hardcoded — so the glyph
   takes on each enemy's roster color, matching Debono's semantic-color system.

   The set coheres through a shared visual GRAMMAR (oscillation, stacking,
   division/cascade, cycle/spiral, queue/timeline, attenuation/occlusion,
   convergence) — a shared language, not a shared glyph. That is the per-INSTANCE
   uniqueness the makeover needs: every boss reads distinctly.

   Keyed by enemy class (lowercase) — matches node.boss_class /
   node.meta.enemy.class set by drug_lab_map_builder.py. Roster source of truth:
   backend/services/mid_boss_roster.py (17) + final_boss_roster.py (Septyx).

   Rendered inside ProgressMap's node <g>, which supplies stroke + weight + the
   `color` CSS var (so fill="currentColor" dots take the enemy color). Filled
   marks set fill="currentColor"; faint guide marks set opacity.
   ────────────────────────────────────────────────────────────────────────── */

export const BOSS_GLYPHS = {
  // ── Oscillation / balance ───────────────────────────────────────────────
  // Glyca — glucose oscillation held inside the euglycemia safe band.
  glyca: (
    <>
      <line x1="2.5" y1="8" x2="21.5" y2="8" opacity={0.32} />
      <line x1="2.5" y1="16" x2="21.5" y2="16" opacity={0.32} />
      <path d="M2.5 12 C5 6 7 6 9 12 S13 18 15.5 12 S20 6 21.5 12" />
    </>
  ),
  // Hemora — seesaw teetering between clot breach and bleed breach.
  hemora: (
    <>
      <path d="M8 20 L12 13.5 L16 20" />
      <line x1="3" y1="8.6" x2="21" y2="13.4" />
      <line x1="3" y1="8.6" x2="3" y2="11.6" />
      <circle cx="3" cy="12.9" r="1.5" fill="currentColor" stroke="none" />
      <line x1="21" y1="13.4" x2="21" y2="16.4" />
      <circle cx="21" cy="17.7" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  // Bradyx — levodopa pulse interrupted by a wearing-off freeze gap.
  bradyx: (
    <>
      <path d="M2 12 H5 L6.5 6 L8 18 L9.5 12 H11" />
      <path d="M11 12 H14.5" opacity={0.5} strokeDasharray="1.6 1.6" />
      <path d="M14.5 12 H16 L17.5 6 L19 18 L20.5 12 H22" />
    </>
  ),

  // ── Stacking / escalation ────────────────────────────────────────────────
  // Chroni — obstruction ratchets down a step each round and never climbs back,
  // coming to rest ABOVE the baseline rather than on it. The gap between the
  // resting dot and the faint floor line IS the mark: this fight's signature is
  // that relief has a floor it cannot pass, so the mark is monotone decline
  // arrested short of zero, never the oscillation grammar (Glyca, Hemora).
  // Deliberately NOT a squeezed airway — that is pathophysiology, and it was the
  // retired Pulm mark. Do not redraw it back into a literal lumen.
  chroni: (
    <>
      <path d="M2.5 4.5 H7.5 V9 H12.5 V13.5 H17.5 V18 H21.5" />
      <line x1="2.5" y1="20.8" x2="21.5" y2="20.8" opacity={0.35} />
      <circle cx="20.6" cy="18" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  // Anaphyl — Gell & Coombs Type I→IV escalating cascade, capped by a surge.
  anaphyl: (
    <>
      <path d="M3 20 H21" />
      <path d="M6 20 V16" />
      <path d="M10.5 20 V12.5" />
      <path d="M15 20 V9" />
      <path d="M19.5 20 V5.6" />
      <path d="M17.4 7.4 L19.5 5 L21.6 7.4" />
    </>
  ),
  // Athero — lipid plaque builds top and bottom until the lumen is a trickle.
  athero: (
    <>
      <path d="M3 7 H21" />
      <path d="M3 17 H21" />
      <path d="M8 7 Q12 13.4 16 7" />
      <path d="M8 17 Q12 10.6 16 17" />
      <circle cx="12" cy="12" r="0.7" fill="currentColor" stroke="none" />
    </>
  ),

  // ── Division / cascade ───────────────────────────────────────────────────
  // Mitos — one clone dividing into two (proliferation under treatment).
  mitos: (
    <>
      <circle cx="8" cy="12" r="5.2" />
      <circle cx="16" cy="12" r="5.2" />
      <path d="M12 6.9 V17.1" />
      <circle cx="8" cy="12" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="16" cy="12" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // Coelo — one mistreatment cascades into many spawned complications.
  coelo: (
    <>
      <circle cx="12" cy="4.6" r="1.6" />
      <path d="M12 6.2 L7.5 10.8" />
      <path d="M12 6.2 L16.5 10.8" />
      <circle cx="7.5" cy="12.4" r="1.4" />
      <circle cx="16.5" cy="12.4" r="1.4" />
      <path d="M7.5 13.8 L5 18.4" />
      <path d="M7.5 13.8 L10 18.4" />
      <path d="M16.5 13.8 L14 18.4" />
      <path d="M16.5 13.8 L19 18.4" />
      <circle cx="5" cy="19.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="19.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="14" cy="19.8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="19.8" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),

  // ── Cycle / spiral ───────────────────────────────────────────────────────
  // Cephalgia — migraine recurrence: the cycle that keeps returning.
  cephalgia: (
    <>
      <path d="M19.2 8.4 A8 8 0 1 1 16.8 4.9" />
      <path d="M16.2 4.2 L20.4 4.4 L19.7 8.2" />
      <circle cx="4.2" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="9" cy="20" r="1" fill="currentColor" stroke="none" />
      <circle cx="8" cy="4.6" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  // Algo — opioid tolerance spiral: escalating dose, diminishing return.
  algo: (
    <>
      <path d="M12 12 a1.6 1.6 0 1 1 1.7 -1.6 a3.7 3.7 0 1 1 -3.8 -3.6 a5.8 5.8 0 1 1 -5.7 6 a7.9 7.9 0 1 1 8 7.7" />
    </>
  ),

  // ── Queue / timeline ─────────────────────────────────────────────────────
  // Synap — delayed onset: a clock over a queue that fires late.
  synap: (
    <>
      <circle cx="12" cy="9.5" r="6.4" />
      <path d="M12 9.5 V5.4" />
      <path d="M12 9.5 L15.4 11" />
      <line x1="6.5" y1="20" x2="17.5" y2="20" opacity={0.3} />
      <circle cx="7" cy="20" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="20" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="17" cy="20" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  // Gravida — gestational timeline with gated trimester windows.
  gravida: (
    <>
      <path d="M3 17.5 H20" />
      <path d="M18 15.5 L20.6 17.5 L18 19.5" />
      <path d="M8 15 V20" opacity={0.5} />
      <path d="M14 15 V20" opacity={0.5} />
      <circle cx="5.5" cy="10.5" r="1.2" />
      <circle cx="11" cy="9.6" r="2.1" />
      <circle cx="17" cy="8.4" r="3" />
    </>
  ),
  // Convul — misfiring cortex: an erratic spike-train demanding protocol order.
  convul: (
    <>
      <path d="M2 14 L4.5 8 L6 15 L7.5 5 L9.5 15 L11 9 L13 15 L14.5 6 L16.5 15 L18 10 L20 15 L22 13" />
    </>
  ),

  // ── Attenuation / occlusion / selection ──────────────────────────────────
  // Nephra — failing filter: full inflow, weak trickle out as GFR decays.
  nephra: (
    <>
      <circle cx="9" cy="3.2" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="13" cy="3.2" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="11" cy="2" r="0.9" fill="currentColor" stroke="none" />
      <path d="M4 5.5 H20 L13.5 13 V19 L10.5 21 V13 Z" />
      <path d="M6.6 8.4 H17.4" opacity={0.5} />
      <circle cx="11.7" cy="22.4" r="0.6" fill="currentColor" stroke="none" opacity={0.6} />
    </>
  ),
  // Visia — aperture closing down: vision progressively veiled over.
  visia: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5 L12 9" />
      <path d="M20.4 12 L15 12" />
      <path d="M5.5 16.5 L9.2 13.9" />
      <path d="M16.3 19 L13.7 15.3" />
      <path d="M6.8 6.4 L9.6 9.2" />
    </>
  ),
  // Pestil — selection pressure: many colonies fall, one resists.
  pestil: (
    <>
      <circle cx="6" cy="4.4" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="4.4" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="18" cy="4.4" r="1.1" fill="currentColor" stroke="none" />
      <path d="M4 8.5 L12 16 L20 8.5" />
      <circle cx="12" cy="19.6" r="2.5" />
      <circle cx="12" cy="19.6" r="0.9" fill="currentColor" stroke="none" />
    </>
  ),

  // ── Convergence (final) ──────────────────────────────────────────────────
  // Septyx — the capstone: every domain converges to one point.
  septyx: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2 V8" />
      <path d="M12 22 V16" />
      <path d="M2 12 H8" />
      <path d="M22 12 H16" />
      <path d="M4.9 4.9 L8.1 8.1" />
      <path d="M19.1 4.9 L15.9 8.1" />
      <path d="M4.9 19.1 L8.1 15.9" />
      <path d="M19.1 19.1 L15.9 15.9" />
    </>
  ),
};

// Class aliases → canonical glyph key. (Athero's pool name is "lurker";
// Septyx's roster class is "phantom".)
const GLYPH_ALIASES = { phantom: 'septyx', lurker: 'athero' };

/* Resolve the bespoke glyph for a boss node by its enemy identity. Tries
   node.boss_class, then node.meta.enemy.class / .icon, with a final type-level
   fallback to Septyx for the single final_boss node (which carries color but
   no boss_class). Returns the JSX fragment, or null when the node has no
   roster enemy (e.g. generic cluster bosses) — caller then keeps the original
   per-type glyph. */
export function getBossGlyph(node) {
  if (!node) return null;
  const raw =
    node.boss_class ||
    (node.meta && node.meta.enemy && (node.meta.enemy.class || node.meta.enemy.icon)) ||
    (node.type === 'final_boss' ? 'septyx' : '');
  const key = String(raw).toLowerCase();
  return BOSS_GLYPHS[GLYPH_ALIASES[key] || key]
    || (node.type === 'final_boss' ? BOSS_GLYPHS.septyx : null);
}
